// =============================================
// useMusicPlayer — one persistent <audio> + playback state for the whole music area
// =============================================
// Owns a single audio element (rendered once by MusicPage) so playback survives
// navigation between the full Now-Playing page and the browsing views. Resolves each
// track's blob from IndexedDB to an object URL (revoked on change), and exposes
// play/pause/seek/next/prev plus volume, repeat (off/all/one) and shuffle.
//
// A hook can't render JSX, so the consumer renders:
//   <audio ref={p.audioRef} onTimeUpdate={p.onTimeUpdate} onLoadedMetadata={p.onLoadedMetadata}
//          onEnded={p.onEnded} onPlay={p.onPlay} onPause={p.onPause} />
// =============================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { getAudioUrl } from '../../lib/audioStore';
import type { TrackView } from './musicLibrary';

export type RepeatMode = 'off' | 'all' | 'one';

export interface MusicPlayerApi {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  track: TrackView | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  repeat: RepeatMode;
  shuffle: boolean;
  missing: boolean;
  hasTrack: boolean;
  hasNext: boolean;
  hasPrev: boolean;
  togglePlay: () => void;
  seek: (t: number) => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  cycleRepeat: () => void;
  toggleShuffle: () => void;
  next: () => void;
  prev: () => void;
  // wire these onto the <audio> element
  onTimeUpdate: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
  onLoadedMetadata: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
  onEnded: () => void;
  onPlay: () => void;
  onPause: () => void;
}

export function useMusicPlayer(
  queue: TrackView[],
  index: number,
  setIndex: (i: number) => void,
  onPlayLogged?: (t: TrackView) => void,
): MusicPlayerApi {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [muted, setMuted] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');
  const [shuffle, setShuffle] = useState(false);
  const [missing, setMissing] = useState(false);
  const loggedRef = useRef<number | null>(null);
  // Keep refs to mutable state the event handlers need without re-binding them.
  const repeatRef = useRef(repeat); repeatRef.current = repeat;
  const shuffleRef = useRef(shuffle); shuffleRef.current = shuffle;

  const track = index >= 0 && index < queue.length ? queue[index] : null;
  const hasTrack = !!track;
  const hasNext = index >= 0 && index < queue.length - 1;
  const hasPrev = index > 0;

  // Load the current track's blob → object URL whenever the track changes.
  useEffect(() => {
    if (!track) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setMissing(false);
    setCurrentTime(0);
    setDuration(track.durationSeconds || 0);
    (async () => {
      const url = await getAudioUrl(track.storageKey);
      if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
      if (!url) { setMissing(true); setIsPlaying(false); return; }
      objectUrl = url;
      const el = audioRef.current;
      if (el) {
        el.src = url;
        el.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        if (loggedRef.current !== track.trackId) {
          loggedRef.current = track.trackId;
          onPlayLogged?.(track);
        }
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.trackId]);

  // Apply volume / mute to the element.
  useEffect(() => {
    const el = audioRef.current;
    if (el) el.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !track || missing) return;
    if (el.paused) el.play().then(() => setIsPlaying(true)).catch(() => {});
    else { el.pause(); setIsPlaying(false); }
  }, [track, missing]);

  const seek = useCallback((t: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = t;
    setCurrentTime(t);
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    if (v > 0) setMuted(false);
  }, []);
  const toggleMute = useCallback(() => setMuted(m => !m), []);
  const cycleRepeat = useCallback(() => setRepeat(r => (r === 'off' ? 'all' : r === 'all' ? 'one' : 'off')), []);
  const toggleShuffle = useCallback(() => setShuffle(s => !s), []);

  const next = useCallback(() => {
    if (queue.length === 0) return;
    if (shuffleRef.current && queue.length > 1) {
      let r = index;
      // deterministic-enough random next that isn't the current track
      while (r === index) r = Math.floor(Math.random() * queue.length); // eslint-disable-line
      setIndex(r);
    } else if (index < queue.length - 1) {
      setIndex(index + 1);
    } else if (repeatRef.current === 'all') {
      setIndex(0);
    }
  }, [queue.length, index, setIndex]);

  const prev = useCallback(() => {
    const el = audioRef.current;
    if (el && el.currentTime > 3) { el.currentTime = 0; setCurrentTime(0); return; }
    if (index > 0) setIndex(index - 1);
    else if (repeatRef.current === 'all') setIndex(queue.length - 1);
  }, [index, queue.length, setIndex]);

  // ---- audio element event handlers ----
  const onTimeUpdate = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    setCurrentTime(e.currentTarget.currentTime);
  }, []);
  const onLoadedMetadata = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
    setDuration(e.currentTarget.duration || 0);
  }, []);
  const onEnded = useCallback(() => {
    const el = audioRef.current;
    if (repeatRef.current === 'one' && el) { el.currentTime = 0; el.play().catch(() => {}); return; }
    if (shuffleRef.current && queue.length > 1) { next(); return; }
    if (index < queue.length - 1) next();
    else if (repeatRef.current === 'all') setIndex(0);
    else setIsPlaying(false);
  }, [index, queue.length, next, setIndex]);
  const onPlay = useCallback(() => setIsPlaying(true), []);
  const onPause = useCallback(() => setIsPlaying(false), []);

  return {
    audioRef, track, isPlaying, currentTime, duration, volume, muted, repeat, shuffle, missing,
    hasTrack, hasNext, hasPrev,
    togglePlay, seek, setVolume, toggleMute, cycleRepeat, toggleShuffle, next, prev,
    onTimeUpdate, onLoadedMetadata, onEnded, onPlay, onPause,
  };
}
